import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PublicPage } from '@/components/ui/layout/public-page';
import { queryClient } from '@/main';
import { acceptInvitationMutation } from '@/mutations/auth/acceptInvitationMutation';
import { getPreviewOrganizationQuery } from '@/queries/getPreviewOrganizationQuery';
import { getQueryKeysFactory } from '@/queries/queryKeysFactory';
import { requiredInvitationSearchSchema } from '@/routes/invitation';

export const Route = createFileRoute('/_public/organization/join')({
  component: RouteComponent,
  validateSearch: (search) => requiredInvitationSearchSchema.parse(search),
});

function RouteComponent() {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { organizationId, invitationCode } = Route.useSearch();
  const { userKeys } = getQueryKeysFactory();

  const { data: previewOrganization } = useSuspenseQuery(
    getPreviewOrganizationQuery({
      organizationId,
      invitationCode,
    })
  );

  const { mutate: acceptInvitation, isPending } = useMutation({
    ...acceptInvitationMutation({
      organizationId,
      invitationCode,
    }),
    onSuccess: async (response) => {
      if (response.isOk()) {
        await queryClient.refetchQueries({ queryKey: userKeys.membershipList });
        navigate({ to: '/dashboard' });
        toast.success('Joined organization');
      } else {
        if (response.error.type === 'INVALID_INVITATION') {
          setError('Error during the validation of the invitation');
        }

        if (response.error.type === 'INVALID_INVITATION_WRONG_EMAIL') {
          setError(
            'You cannot join the organization, please make sure to connect with the right email'
          );
        }
      }
    },
  });

  return (
    <PublicPage.Layout>
      <PublicPage.Header />
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl">
            Join {previewOrganization.name}
          </CardTitle>
          <CardDescription>
            Accept the invitation to join this organization and collaborate with
            your team members.
            {error && (
              <>
                <br />
                <span className="text-red-500 mt-1.5">{error}</span>
              </>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Button
            className="w-full"
            size="lg"
            disabled={isPending}
            onClick={() => acceptInvitation()}
          >
            Join
          </Button>
        </CardContent>
      </Card>
    </PublicPage.Layout>
  );
}
